import React, {useState, useEffect} from "react";
import {useParams, useNavigate} from "react-router-dom"; // Import useNavigate
import {candleTypes} from "./TributeCreator";
import TributeViewer from "./TributeViewer";
import ShareModal from "./ShareModal";

function ReadTribute() {
    const {id} = useParams();
    const navigate = useNavigate(); // Hook for navigation
    const [tributeData, setTributeData] = useState(null);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [paymentStatus, setPaymentStatus] = useState(null); // State for payment status

    useEffect(() => {
        const fetchTribute = async () => {
            setIsLoading(true);
            setError('');
            setTributeData(null);
            setPaymentStatus(null);

            if (!id) {
                setError("Brak ID znicza w linku.");
                setIsLoading(false);
                return;
            }

            try {
                // 1. Attempt to fetch the public tribute
                const tributeResponse = await fetch(`/api/tributes/${id}`);

                if (tributeResponse.ok) {
                    // Scenario 1: Success (Tribute is already TRANSACTION_COMPLETED)
                    const data = await tributeResponse.json();
                    setTributeData(data);
                    setPaymentStatus('TRANSACTION_COMPLETED');
                } else if (tributeResponse.status === 404) {
                    // Scenario 2: Not Found, check payment status
                    try {
                        const statusResponse = await fetch(`/api/tributes/${id}/status`);

                        if (statusResponse.ok) {
                            // Sub-scenario 2a: Status endpoint returned OK
                            const statusData = await statusResponse.json();
                            const status = statusData.tributeStatus;
                            setPaymentStatus(status);

                            if (status === 'TRANSACTION_COMPLETED') {
                                // Payment just completed, set data
                                setTributeData(statusData);
                            }
                            // Other statuses (PENDING, FAILED) will be handled by render logic
                        } else if (statusResponse.status === 404) {
                            // Sub-scenario 2b: Status also 404, tribute does not exist
                            navigate('/'); // Redirect to home
                        } else {
                            // Status check failed with another error
                            throw new Error('Błąd podczas sprawdzania statusu płatności. Stwórz nowy znicz lub skontaktuj się z nami.');
                        }
                    } catch (statusError) {
                        console.error("Error fetching tribute status:", statusError);
                        setError(statusError.message || "Nie udało się sprawdzić statusu znicza.");
                    }
                } else {
                    // Scenario 3: First request failed with something other than 404
                    throw new Error(`Błąd serwera: ${tributeResponse.status}`);
                }
            } catch (e) {
                console.error("Error fetching tribute data:", e);
                setError(e.message || "Nie udało się załadować danych znicza.");
                setTributeData(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTribute();
    }, [id, navigate]);

    // 1. Render Loading
    if (isLoading) {
        return <div className="read-tribute-container"><h2>Ładowanie znicza...</h2></div>;
    }

    // 2. Render Error (if any)
    if (error) {
        return <div className="read-tribute-container"><h2>{error}</h2></div>;
    }

    // 3. Render based on Payment Status
    switch (paymentStatus) {
        case 'TRANSACTION_COMPLETED':
            if (tributeData) {
                const selectedCandle = tributeData.candleNumber
                    ? candleTypes.find((c) => c.id === tributeData.candleNumber)
                    : null;

                if (!selectedCandle) {
                    console.warn(`Znicz o ID "${tributeData.candleNumber}" nie został znaleziony lokalnie.`);
                    // Handle missing candle, but still show text
                }

                const shareLink = typeof window !== "undefined" ? window.location.href : "";

                return (
                    <div className="read-tribute-container">
                        <TributeViewer
                            personName={tributeData.recipientName}
                            memorialMessage={tributeData.memorialMessage}
                            selectedCandle={selectedCandle}
                            fromName={tributeData.senderName}
                        />
                        <div className="read-tribute-actions">
                            <button
                                type="button"
                                className="download-button read-tribute-share"
                                onClick={() => setIsShareModalOpen(true)}
                            >
                                {"Udostępnij lub pobierz"}
                            </button>
                            <a
                                href="/#tribute-creator"
                                className="download-button read-tribute-cta"
                            >
                                Zapal nowy znicz
                            </a>
                        </div>
                        {isShareModalOpen && (
                            <ShareModal
                                link={shareLink}
                                personName={tributeData.recipientName}
                                memorialMessage={tributeData.memorialMessage}
                                onClose={() => setIsShareModalOpen(false)}
                            />
                        )}
                    </div>
                );
            }
            // Fallthrough if tributeData is null (shouldn't happen if status is COMPLETED)
            break; // Added break for clarity

        case 'TRANSACTION_PENDING':
            return (
                <div className="read-tribute-container">
                    <h2>Czekamy na potwierdzenie transakcji...</h2>
                    <p>Płatność jest w trakcie przetwarzania. Odśwież stronę za chwilę.</p>
                </div>
            );

        // All other cases (FAILED, CANCELLED, BANNED, or null)
        default:
            return (
                <div className="read-tribute-container">
                    <h2>Płatność nie została ukończona</h2>
                    <p>Wymagane jest ponowne stworzenie znicza. W razie problemów prosimy o kontakt.</p>
                    <div className="read-tribute-actions">
                        <a
                            href="/"
                            className="download-button read-tribute-cta"
                        >
                            Stwórz nowy znicz
                        </a>
                    </div>
                </div>
            );
    }
}

export default ReadTribute;